import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { axiosClientSignIn } from '../axios/api';

// Plain axios instance for the direct-to-S3 PUT (presigned URL, no auth cookies needed)
const s3Axios = axios.create({ withCredentials: false, headers: {} });

// Upload a training video into a course: get presigned URL -> PUT bytes to S3 -> save metadata
export const uploadTrainingVideo = createAsyncThunk(
  'trainingVideos/upload',
  async ({ courseId, order, file, title, description }, { dispatch, rejectWithValue }) => {
    try {
      const { data: urlData } = await axiosClientSignIn.post('/api/training-videos/upload-url', {
        fileName: file.name,
        mimeType: file.type
      });

      await s3Axios.put(urlData.uploadUrl, file, {
        headers: { 'Content-Type': file.type },
        onUploadProgress: (event) => {
          const percent = Math.round((event.loaded * 100) / event.total);
          dispatch(setUploadProgress(percent));
        }
      });

      const response = await axiosClientSignIn.post('/api/training-videos', {
        courseId,
        order,
        title,
        description,
        s3Key: urlData.s3Key,
        s3Bucket: urlData.s3Bucket,
        s3Url: urlData.s3Url,
        mimeType: file.type,
        size: file.size
      });

      return response.data.video;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload video');
    }
  }
);

// Delete (soft) a video
export const deleteTrainingVideo = createAsyncThunk(
  'trainingVideos/delete',
  async (videoId, { rejectWithValue }) => {
    try {
      await axiosClientSignIn.delete(`/api/training-videos/${videoId}`);
      return videoId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete video');
    }
  }
);

// Report watch progress / completion for the current user
export const updateTrainingProgress = createAsyncThunk(
  'trainingVideos/updateProgress',
  async ({ videoId, progressPercent, watchedSeconds, completed }, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.post(`/api/training-videos/${videoId}/progress`, {
        progressPercent,
        watchedSeconds,
        completed
      });
      return { videoId, progress: response.data.progress };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update progress');
    }
  }
);

const initialState = {
  uploading: false,
  uploadProgress: 0,
  error: null
};

const trainingVideoSlice = createSlice({
  name: 'trainingVideos',
  initialState,
  reducers: {
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    clearTrainingVideoError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadTrainingVideo.pending, (state) => {
        state.uploading = true;
        state.uploadProgress = 0;
        state.error = null;
      })
      .addCase(uploadTrainingVideo.fulfilled, (state) => {
        state.uploading = false;
        state.uploadProgress = 100;
      })
      .addCase(uploadTrainingVideo.rejected, (state, action) => {
        state.uploading = false;
        state.uploadProgress = 0;
        state.error = action.payload;
      });
  }
});

export const { setUploadProgress, clearTrainingVideoError } = trainingVideoSlice.actions;
export default trainingVideoSlice.reducer;
